<template>
  <div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="礼物配置" name="catalog">
        <ContentWrap title="礼物配置（对齐 PHP：图标 / SVGA / 金币价 / 启用）">
          <el-row :gutter="10" class="mb8">
            <el-col :span="1.5">
              <el-button type="primary" plain @click="handleAdd" v-hasPermi="['live:gift:update']">
                <Icon icon="ep:plus" />新增
              </el-button>
            </el-col>
          </el-row>
        </ContentWrap>

        <ContentWrap>
          <el-table v-loading="catalogLoading" :data="catalogList" border>
            <el-table-column label="编号" prop="id" width="80" />
            <el-table-column label="礼物名称" prop="name" min-width="120" />
            <el-table-column label="图片" min-width="90">
              <template #default="scope">
                <el-image
                  v-if="scope.row.picUrl"
                  :src="scope.row.picUrl"
                  style="width: 40px; height: 40px; border-radius: 4px"
                  fit="cover"
                />
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column label="SVGA" min-width="120" show-overflow-tooltip>
              <template #default="scope">
                <span v-if="scope.row.svgaUrl">已上传</span>
                <span v-else-if="scope.row.effectKey">本地:{{ scope.row.effectKey }}</span>
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column label="特效KEY" prop="effectKey" width="100" />
            <el-table-column label="金币价格" prop="coinPrice" width="100" />
            <el-table-column label="排序" prop="sort" width="80" />
            <el-table-column label="状态" prop="enabled" width="90">
              <template #default="scope">
                <el-tag :type="scope.row.enabled ? 'success' : 'info'">{{
                  scope.row.enabled ? '启用' : '停用'
                }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column
              label="创建时间"
              prop="createTime"
              min-width="180"
              :formatter="dateFormatter"
            />
            <el-table-column label="操作" width="180" fixed="right">
              <template #default="scope">
                <el-button
                  link
                  type="primary"
                  @click="handleEdit(scope.row)"
                  v-hasPermi="['live:gift:update']"
                  >修改</el-button
                >
                <el-button
                  link
                  type="danger"
                  @click="handleDelete(scope.row)"
                  v-hasPermi="['live:gift:delete']"
                  >删除</el-button
                >
              </template>
            </el-table-column>
          </el-table>

          <pagination
            v-show="catalogTotal > 0"
            :total="catalogTotal"
            v-model:page="catalogQuery.pageNo"
            v-model:limit="catalogQuery.pageSize"
            @pagination="getCatalogList"
          />
        </ContentWrap>
      </el-tab-pane>

      <el-tab-pane label="送礼记录" name="record">
        <ContentWrap title="送礼记录">
          <el-form :inline="true" class="mb8">
            <el-form-item label="直播间">
              <LiveRoomIdSelect v-model="recordQuery.liveId" select-class="!w-260px" />
            </el-form-item>
            <el-form-item label="送礼用户">
              <LiveMemberUserSelect v-model="recordQuery.userId" select-class="!w-260px" />
            </el-form-item>
            <el-form-item label="主播">
              <LiveAnchorUserSelect
                v-model="recordQuery.anchorUserId"
                select-class="!w-260px"
                :status="undefined"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="queryRecords">
                <Icon icon="ep:search" />查询
              </el-button>
              <el-button @click="resetRecordQuery"> <Icon icon="ep:refresh" />重置 </el-button>
            </el-form-item>
          </el-form>
        </ContentWrap>

        <ContentWrap>
          <el-table v-loading="recordLoading" :data="recordList" border>
            <el-table-column label="直播间ID" prop="liveId" width="100" align="center" />
            <el-table-column label="主播会员账号" min-width="140">
              <template #default="{ row }">
                <div>{{ row.anchorAccount || '--' }}</div>
                <div v-if="row.anchorUserId" class="sub-text">UID {{ row.anchorUserId }}</div>
              </template>
            </el-table-column>
            <el-table-column label="获得礼物名称" prop="giftName" min-width="120" show-overflow-tooltip>
              <template #default="{ row }">{{ row.giftName || '--' }}</template>
            </el-table-column>
            <el-table-column label="礼物图片" width="90" align="center">
              <template #default="{ row }">
                <el-image
                  v-if="row.giftPic"
                  :src="row.giftPic"
                  :preview-src-list="[row.giftPic]"
                  preview-teleported
                  fit="cover"
                  class="gift-thumb"
                />
                <span v-else class="sub-text">无</span>
              </template>
            </el-table-column>
            <el-table-column label="送礼会员" min-width="140">
              <template #default="{ row }">
                <div>{{ row.senderAccount || '--' }}</div>
                <div v-if="row.userId" class="sub-text">UID {{ row.userId }}</div>
              </template>
            </el-table-column>
            <el-table-column
              label="赠送时间"
              prop="createTime"
              width="170"
              :formatter="dateFormatter"
            />
            <el-table-column label="礼物金币" prop="amount" width="100" align="right">
              <template #default="{ row }">{{ formatCoin(row.amount) }}</template>
            </el-table-column>
            <el-table-column label="主播获得金币" prop="anchorCoin" width="120" align="right">
              <template #default="{ row }">{{ formatCoin(row.anchorCoin) }}</template>
            </el-table-column>
            <el-table-column label="金币转化比例" width="120" align="center">
              <template #default="{ row }">{{ formatRatio(row.coinShareRatioPercent) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="90" fixed="right" align="center">
              <template #default="{ row }">
                <el-button
                  link
                  type="danger"
                  v-hasPermi="['live:gift:delete']"
                  @click="handleDeleteRecord(row)"
                >删除</el-button>
              </template>
            </el-table-column>
          </el-table>

          <pagination
            v-show="recordTotal > 0"
            :total="recordTotal"
            v-model:page="recordQuery.pageNo"
            v-model:limit="recordQuery.pageSize"
            @pagination="getRecordList"
          />
        </ContentWrap>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="dialogTitle" v-model="dialogVisible" width="560px" append-to-body>
      <el-form
        ref="formRef"
        :model="form"
        :rules="withLiveInputLimits(rules, liveInputLimits)"
        label-width="110px"
      >
        <el-form-item label="礼物名称" prop="name">
          <el-input
            show-word-limit
            :maxlength="64"
            v-model="form.name"
            placeholder="请输入礼物名称"
          />
        </el-form-item>
        <el-form-item label="金币价格" prop="coinPrice">
          <el-input-number
            :precision="0"
            v-model="form.coinPrice"
            :min="0"
            :max="999999"
            controls-position="right"
          />
          <span class="form-tip">对齐 PHP need_gold，用户送礼扣会员金币</span>
        </el-form-item>
        <el-form-item label="封面图" prop="picUrl">
          <UploadImg v-model="form.picUrl" />
          <span class="form-tip"
            >列表缩略图。可不传：H5 会用特效 KEY 对应本地 SVGA 抽出的封面（covers/{KEY}.png）</span
          >
        </el-form-item>
        <el-form-item label="SVGA动效" prop="svgaUrl">
          <UploadFile
            v-model="form.svgaUrl"
            :limit="1"
            :file-size="15"
            :file-type="['svga']"
            :replace-on-exceed="true"
            directory="live/gift/svga"
            :use-material-library="false"
          />
          <span class="form-tip">上传 .svga 后 H5 播全屏特效；启用礼物必须填 SVGA 或特效 KEY</span>
        </el-form-item>
        <el-form-item label="特效KEY" prop="effectKey">
          <el-input
            show-word-limit
            :maxlength="64"
            v-model="form.effectKey"
            placeholder="如 heart / rose / rocket / dragon / Goldteam"
          />
          <span class="form-tip"
            >主播端 TIM introduce；对应 H5 本地 /static/live/svga/{KEY}.svga 与封面
            covers/{KEY}.png</span
          >
        </el-form-item>
        <el-form-item label="简介" prop="intro">
          <el-input
            show-word-limit
            :maxlength="255"
            v-model="form.intro"
            type="textarea"
            :rows="2"
            placeholder="可选简介"
          />
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number
            :precision="0"
            v-model="form.sort"
            :min="0"
            :max="9999"
            controls-position="right"
          />
        </el-form-item>
        <el-form-item label="状态" prop="enabled">
          <el-radio-group v-model="form.enabled">
            <el-radio :label="true">启用</el-radio>
            <el-radio :label="false">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { withLiveInputLimits } from '@/utils/liveInputLimits'
const liveInputLimits = { name: 64, effectKey: 64, intro: 255, picUrl: 512, svgaUrl: 512 }

import { dateFormatter } from '@/utils/formatTime'
import { ref, reactive, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  catalogPage,
  saveCatalog,
  deleteCatalog,
  recordPage,
  deleteRecord
} from '@/api/live/gift'
import { UploadImg, UploadFile } from '@/components/UploadFile'
import LiveRoomIdSelect from '@/views/live/components/LiveRoomIdSelect.vue'
import LiveMemberUserSelect from '@/views/live/components/LiveMemberUserSelect.vue'
import LiveAnchorUserSelect from '@/views/live/components/LiveAnchorUserSelect.vue'

const route = useRoute()
/** 侧栏「送礼记录」走 gift-record 路径，或 ?tab=record */
const resolveGiftTab = () => {
  const q = String(route.query.tab || '')
  if (q === 'record') return 'record'
  const path = String(route.path || '')
  if (path.includes('gift-record')) return 'record'
  const name = String(route.name || '')
  if (name.includes('GiftRecord') || name.includes('gift-record')) return 'record'
  return 'catalog'
}
const activeTab = ref(resolveGiftTab())

const catalogLoading = ref(false)
const catalogList = ref<any[]>([])
const catalogTotal = ref(0)
const catalogQuery = reactive({ pageNo: 1, pageSize: 10 })

const recordLoading = ref(false)
const recordList = ref<any[]>([])
const recordTotal = ref(0)
const recordQuery = reactive({
  pageNo: 1,
  pageSize: 10,
  liveId: undefined as number | undefined,
  userId: undefined as number | undefined,
  anchorUserId: undefined as number | undefined
})

const dialogVisible = ref(false)
const dialogTitle = ref('')
const formRef = ref<any>(null)

const form = reactive({
  id: undefined as number | undefined,
  name: '',
  picUrl: '',
  intro: '',
  svgaUrl: '',
  effectKey: '',
  coinPrice: 0,
  sort: 0,
  enabled: true
})

const rules = {
  name: [{ required: true, message: '礼物名称不能为空', trigger: 'blur' }],
  coinPrice: [{ required: true, message: '金币价格不能为空', trigger: 'change' }],
  enabled: [{ required: true, message: '状态不能为空', trigger: 'change' }]
}

const getCatalogList = async () => {
  catalogLoading.value = true
  try {
    const data: any = await catalogPage(catalogQuery)
    catalogList.value = data.list || []
    catalogTotal.value = data.total || 0
  } finally {
    catalogLoading.value = false
  }
}

const getRecordList = async () => {
  recordLoading.value = true
  try {
    const params: any = {
      pageNo: recordQuery.pageNo,
      pageSize: recordQuery.pageSize
    }
    if (recordQuery.liveId != null) params.liveId = recordQuery.liveId
    if (recordQuery.userId != null) params.userId = recordQuery.userId
    if (recordQuery.anchorUserId != null) params.anchorUserId = recordQuery.anchorUserId
    const data: any = await recordPage(params)
    recordList.value = data.list || []
    recordTotal.value = data.total || 0
  } finally {
    recordLoading.value = false
  }
}

const queryRecords = () => {
  recordQuery.pageNo = 1
  getRecordList()
}

const resetRecordQuery = () => {
  recordQuery.liveId = undefined
  recordQuery.userId = undefined
  recordQuery.anchorUserId = undefined
  recordQuery.pageNo = 1
  getRecordList()
}

const resetForm = () => {
  form.id = undefined
  form.name = ''
  form.picUrl = ''
  form.intro = ''
  form.svgaUrl = ''
  form.effectKey = ''
  form.coinPrice = 0
  form.sort = 0
  form.enabled = true
}

const handleAdd = () => {
  resetForm()
  dialogTitle.value = '新增礼物'
  dialogVisible.value = true
}

const handleEdit = (row: any) => {
  resetForm()
  Object.assign(form, row)
  dialogTitle.value = '修改礼物'
  dialogVisible.value = true
}

const submitForm = async () => {
  await formRef.value?.validate()
  if (form.enabled && !String(form.svgaUrl || '').trim() && !String(form.effectKey || '').trim()) {
    ElMessage.warning('启用礼物须配置 SVGA 动效或特效 KEY')
    return
  }
  await saveCatalog(form)
  ElMessage.success(form.id ? '修改成功' : '新增成功')
  dialogVisible.value = false
  getCatalogList()
}

const handleDelete = (row: any) => {
  ElMessageBox.confirm('确认删除该礼物吗？', '提示', { type: 'warning' }).then(async () => {
    await deleteCatalog(row.id)
    ElMessage.success('删除成功')
    getCatalogList()
  })
}

const formatCoin = (v: number | string | null | undefined) => {
  if (v == null || v === '') return '--'
  const n = Number(v)
  return Number.isFinite(n) ? n.toFixed(2) : String(v)
}

const formatRatio = (v: number | string | null | undefined) => {
  if (v == null || v === '') return '--'
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}%`
}

const handleDeleteRecord = (row: any) => {
  ElMessageBox.confirm('确认删除该送礼记录吗？删除后不可恢复展示。', '提示', {
    type: 'warning'
  }).then(async () => {
    await deleteRecord(row.id)
    ElMessage.success('删除成功')
    getRecordList()
  })
}

watch(activeTab, (tab) => {
  if (tab === 'record') {
    getRecordList()
  } else if (tab === 'catalog') {
    getCatalogList()
  }
})

watch(
  () => [route.path, route.query.tab, route.name],
  () => {
    activeTab.value = resolveGiftTab()
  }
)

onMounted(() => {
  activeTab.value = resolveGiftTab()
  if (activeTab.value === 'record') {
    getRecordList()
  } else {
    getCatalogList()
  }
})
</script>

<style scoped>
.gift-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.gift-thumb {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #f5f7fa;
}
.sub-text {
  color: var(--el-text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}
.form-tip {
  display: block;
  margin-top: 4px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}
</style>
